import React, { memo, useState } from "react";

import { CheckCircleOutlined, CloseCircleFilled } from "@ant-design/icons";
import PageHelmet from "@Components/shared/PageHelmet";
import { QueryKeys } from "@Constants";
import { IComplianceDetails } from "@Models/IComplianceDetails";
import ComplianceService from "@Services/ComplianceService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, notification, Popconfirm, Space, Table } from "antd";
import moment from "moment";
import { useHistory } from "react-router";

import "@Styles/pages/compliancePage.scss";
const { Column } = Table;

const CompliancePage: React.FC = () => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const [updatedRecords, setUpdatedRecords] = useState<string[]>([]);

    const { isFetching, data } = useQuery(
        [QueryKeys.Compliance],
        async () => {
            const service = new ComplianceService();
            const result = await service.getAll();
            if (!result.hasErrors) return result.value;
            return [];
        },
        {
            refetchOnWindowFocus: false,
            select: (data) => {
                const sortedBySate = data.slice().sort((a, b) => {
                    return new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime();
                });
                return sortedBySate.map((item) => ({
                    ...item,
                    requestedDate: moment(item.requestedDate).format("MMM Do YYYY"),
                }));
            },
        }
    );

    const approve = useMutation(
        async (complianceId: string) => {
            setUpdatedRecords([...updatedRecords, complianceId]);
            const service = new ComplianceService();
            setUpdatedRecords([...updatedRecords.filter((id) => id !== complianceId)]);
            return await service.update(complianceId, "approved");
        },
        {
            onSuccess: async (response) => {
                if (response.hasErrors) {
                    notification.error({ message: `Request has failed` });
                }
                if (!response.hasErrors) {
                    notification.success({ message: `Request approved` });
                    await queryClient.invalidateQueries([QueryKeys.Compliance]);
                }
            },
        }
    );

    const deleteRequest = useMutation(
        async (complianceId: string) => {
            setUpdatedRecords([...updatedRecords, complianceId]);
            const service = new ComplianceService();
            await service.delete(complianceId);
            setUpdatedRecords([...updatedRecords.filter((id) => id !== complianceId)]);
        },
        {
            onSuccess: async (_data, _variables) => {
                notification.success({ message: `Request deleted` });
                await queryClient.invalidateQueries([QueryKeys.Compliance]);
            },
        }
    );

    const getReviewUrl = (record: IComplianceDetails): string => {
        switch (record.entityType) {
            case "activityInstance":
                return `/activity?user=${record.userId}&activity=${record.entityId}`;
            default:
                return "";
        }
    };

    return (
        <article>
            <PageHelmet name={"Compliance"} />
            <section className="compliance-page">
                <Table
                    className={"newUI-glass"}
                    dataSource={data}
                    pagination={{ pageSize: 10, position: ["bottomCenter"] }}
                    loading={isFetching}
                >
                    <Column title="User" dataIndex="fullName" key="fullName" align={"left"} />
                    <Column title="Date" dataIndex="requestedDate" key="date" align={"center"} />
                    <Column
                        title="Request type"
                        dataIndex="type"
                        key="type"
                        align={"center"}
                        render={(_: any, record: IComplianceDetails) => (
                            <span style={{ textTransform: "capitalize" }}>{record.type}</span>
                        )}
                    />
                    <Column
                        title="Action"
                        key="action"
                        align={"center"}
                        render={(_, record: IComplianceDetails) => {
                            const isApproving = approve.isLoading && updatedRecords.includes(record.complianceId);
                            const isFlagged = record.type === "flagged";
                            // TODO: use different wording here for flagged requests
                            return (
                                <Space>
                                    {isFlagged && record.status !== "completed" ? (
                                        // TODO: make it possible to review flagged requests after they've been approved
                                        <Button
                                            className="secondary"
                                            onClick={() => history.push(getReviewUrl(record))}
                                        >
                                            Review
                                        </Button>
                                    ) : null}
                                    <Button
                                        className="secondary"
                                        loading={isApproving}
                                        disabled={record.status === "approved" || record.status === "completed"}
                                        onClick={() => approve.mutate(record.complianceId)}
                                    >
                                        Approve
                                    </Button>
                                </Space>
                            );
                        }}
                    />
                    <Column
                        title="Status"
                        dataIndex="status"
                        key="status"
                        align={"center"}
                        render={(_: any, record: IComplianceDetails) => {
                            if (record.status === "completed") {
                                return (
                                    <span className="compliance-table-item completed">
                                        <CheckCircleOutlined className="completed-icon" />
                                        {record.status}
                                    </span>
                                );
                            }
                            return <span className="compliance-table-item">{record.status}</span>;
                        }}
                    />
                    <Column
                        title="Notes"
                        dataIndex="notes"
                        key="notes"
                        align={"center"}
                        render={(_, record: IComplianceDetails) => (
                            <span
                                className="compliance-table-item"
                                style={{ textTransform: "initial", textAlign: "left" }}
                            >
                                <Space direction="vertical" style={{ maxWidth: "300px", minWidth: "100%" }}>
                                    {record?.notes?.map((note) => (
                                        <span>{note}</span>
                                    ))}
                                </Space>
                            </span>
                        )}
                    />
                    <Column
                        title=""
                        dataIndex="delete"
                        key="delete"
                        align={"center"}
                        render={(_: any, record: IComplianceDetails) => {
                            return (
                                <Popconfirm
                                    title={
                                        record.status === "approved" || record.status === "completed"
                                            ? "Delete this request?"
                                            : "Delete this request? It has not been approved or completed"
                                    }
                                    onConfirm={() => deleteRequest.mutate(record.complianceId)}
                                    okText="Yes"
                                    cancelText="No"
                                    cancelButtonProps={{ className: "secondary" }}
                                >
                                    <CloseCircleFilled title="Delete request" />
                                </Popconfirm>
                            );
                        }}
                    />
                </Table>
            </section>
        </article>
    );
};

export default memo(CompliancePage);
